import { useNavigate } from 'react-router-dom';
import { ProductInformationGrid } from '@ds-mgnl/marketing';
import styles from './FabricPage.module.css';
import { FabricCallToAction } from '../../../components/ProductDetail/FabricCallToAction/FabricCallToAction';
import { PerformanceMaintenanceSummary } from '../../../components/ProductDetail/PerformanceSummary/PerformanceMaintenanceSummary';
import { Table, Image } from '@dsx/react';
import { groupAttributes } from '../../../components/ProductDetail';
import {
	buildCtaNavigation,
	pushFileDownloadEvent,
} from '../../../components/ProductDetail/productAnalytics';
import { useProductPageViewAnalytics } from '../../../hooks/useProductPageViewAnalytics';
import type { ProductResponse } from '../../../services/productApi';

interface FabricPageProps {
	product: ProductResponse;
}

export const FabricPage = ({ product }: FabricPageProps) => {
	const navigate = useNavigate();

	const specSheets =
		product.assets?.['spec-sheet'] ?? product.assets?.['Specification Sheet'];
	const specSheet = specSheets?.[0];

	useProductPageViewAnalytics(product);

	const onRequestSample = () => {
		if (!product?.id) return;

		const destination = buildCtaNavigation({
			path: '/contact/request-sample',
			params: {
				productId: product.id,
				productName: product.name,
				manufacturerNumber: product.manufacturerProductId,
				dsNumber: product.manufacturerCode,
			},
			ctaLabel: 'Request Sample',
			ctaLocation: 'pdp_fabric',
			product,
		});

		navigate(destination);
	};

	const onSpecSheetDownload = () => {
		if (!specSheet?.contentUrl || !product?.id) {
			return;
		}

		pushFileDownloadEvent(product, specSheet);

		window.open(specSheet.contentUrl, '_blank');
	};

	// Use product images if available, otherwise fall back to a placeholder
	const productImages: React.ComponentProps<typeof Image>[] =
		product.images && product.images.length > 0
			? product.images.map((img) => ({
					src: img.contentUrl ?? '',
					alt: img.caption ?? '',
				}))
			: [
					{
						src: 'https://example.com/does-not-exist_z.jpg',
						alt: 'No image available',
					},
				];

	// Performance Summary is sourced from the "Performance Summary" attribute, not
	// the product description (which holds the marketing subhead). When the
	// attribute is absent, no performance data is shown.
	const performanceSummary =
		product.attributes?.['Performance Summary']?.[0]?.valueText;
	const performanceData = performanceSummary
		? { fabricInfo: performanceSummary.split('\n').filter(Boolean) }
		: undefined;

	const cleaningData = {
		cleaningMaintenanceInfo: [
			`Cleaning Code: ${product.attributes?.['CleaningCode']?.[0]?.valueText ?? ''}`,
			product.attributes?.['CleaningStatement']?.[0]?.valueText ?? '',
		].filter(Boolean),
	};

	const attributeRows = product.attributes
		? groupAttributes(product.attributes)
		: [];

	return (
		<div className={styles['product-detail-container']}>
			<div className={styles['main-information-container']}>
				<div className={styles['fabric-image-container']}>
					<Image className={styles['fabric-image']} {...productImages[0]} />
				</div>
				<div className={styles['product-information-container']}>
					<ProductInformationGrid
						productName={product.name ?? ''}
						manufacturer={product.manufacturerName ?? ''}
						manfNumber={product.manufacturerProductId ?? ''}
						shippingInfo={''}
						price={
							product.price
								? product.price.toFixed(2)
								: (product.priceRange ?? '')
						}
					/>
					<FabricCallToAction
						onRequestSample={onRequestSample}
						onSpecSheetDownload={onSpecSheetDownload}
						hasSpecSheet={!!specSheet}
					/>
				</div>
			</div>
			<div className={styles['specifications-container']}>
				<div className={styles['specification-section']}>
					<PerformanceMaintenanceSummary
						performanceData={performanceData}
						cleaningData={cleaningData}
					/>
				</div>
				<div className={styles['specification-section']}>
					<Table
						columns={[
							{ heading: 'Product Details', key: 'label' },
							{ heading: '', key: 'value' },
						]}
						rows={attributeRows}
						rowKey={(row) => row.id}
						onSelect={() => {}}
						outline="none"
					/>
				</div>
			</div>
		</div>
	);
};
